import { useCallback, useEffect, useRef, useState } from "react";
import type { TFunction } from "i18next";
import { fetchFamily, fetchSchoolYears } from "../api";
import type { AppError, EntityId, Family } from "../types";
import { currentSchoolYearFee } from "../utils/familyDetails";
import { mapHttpErrorToMessage } from "../../../utils/mapHttpErrorToMessage";
import {
  getCachedFamily,
  getCachedFamilyDetails,
  setCachedFamilyDetails,
} from "../../familyOverviewCache";

export const useFamilyDetails = (id: EntityId | null | undefined, t: TFunction) => {
  const cachedDetails = id ? getCachedFamilyDetails(id) : null;
  const cachedFamily = id ? getCachedFamily(id) : null;
  const hasInitialCachedDataRef = useRef(Boolean(cachedDetails || cachedFamily));
  const [family, setFamily] = useState<Family | null>(() => cachedDetails?.family ?? cachedFamily ?? null);
  const [currentYearFee, setCurrentYearFee] = useState<number | null>(() => cachedDetails?.currentYearFee ?? null);
  const [loading, setLoading] = useState(() => !cachedDetails && !cachedFamily);
  const [showInitialSpinner, setShowInitialSpinner] = useState(false);
  const [error, setError] = useState<AppError | null>(null);

  const reload = useCallback(async () => {
    if (!id) return;
    setError(null);
    const [familyData, schoolYears] = await Promise.all([fetchFamily(id), fetchSchoolYears().catch(() => [])]);
    const nextCurrentYearFee = currentSchoolYearFee(schoolYears);
    setFamily(familyData);
    setCurrentYearFee(nextCurrentYearFee);
    setCachedFamilyDetails(id, {
      family: familyData,
      currentYearFee: nextCurrentYearFee,
    });
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        if (!hasInitialCachedDataRef.current) setLoading(true);
        await reload();
      } catch (loadError) {
        const mapped = mapHttpErrorToMessage(loadError, t) as AppError;
        if (!cancelled) setError(mapped);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [reload, t]);

  useEffect(() => {
    if (!loading) {
      setShowInitialSpinner(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setShowInitialSpinner(true), 300);
    return () => window.clearTimeout(timer);
  }, [loading]);

  return {
    family,
    setFamily,
    currentYearFee,
    loading,
    showInitialSpinner,
    error,
    reload,
    setError,
  };
};
